<section class="agronomic-panel" *ngIf="moduleId">
  <div class="agronomic-heading">
    <div>
      <h3>{{ 'BALANCE.AGRONOMY.TITLE' | translate }}</h3>
      <p>{{ 'BALANCE.AGRONOMY.INTRO' | translate }}</p>
    </div>
    <span class="catalog-version" *ngIf="profile?.catalogVersion">
      {{ 'BALANCE.AGRONOMY.CATALOG_VERSION' | translate: { version: profile?.catalogVersion } }}
    </span>
  </div>

  <div class="agronomic-error" role="alert" *ngIf="profileError">
    {{ profileError }}
  </div>

  <p class="agronomic-help" role="status" *ngIf="loading">
    {{ 'BALANCE.AGRONOMY.LOADING' | translate }}
  </p>

  <ng-container *ngIf="!loading">
    <div class="agronomic-subsection">
      <h4>{{ 'BALANCE.AGRONOMY.IDENTITY_TITLE' | translate }}</h4>
      <p class="agronomic-help">{{ 'BALANCE.AGRONOMY.IDENTITY_HELP' | translate }}</p>

      <div class="agronomic-fields">
        <mat-form-field appearance="outline">
          <mat-label>{{ 'BALANCE.AGRONOMY.CROP' | translate }}</mat-label>
          <mat-select
            [value]="profileForm.get('agronomicCropCode')?.value"
            (selectionChange)="onCropChange($event.value)"
          >
            <mat-option *ngFor="let crop of cropOptions" [value]="crop.code">
              {{ displayName(crop) }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" *ngIf="selectedCropCode">
          <mat-label>{{ 'BALANCE.AGRONOMY.PROFILE_START' | translate }}</mat-label>
          <input matInput readonly [matDatepicker]="profileStartPicker" [formControl]="profileForm.get('validFrom')" (click)="profileStartPicker.open()" />
          <mat-datepicker-toggle matSuffix [for]="profileStartPicker"></mat-datepicker-toggle>
          <mat-datepicker #profileStartPicker></mat-datepicker>
        </mat-form-field>
      </div>

      <p class="agronomic-status" *ngIf="profile?.identity?.status === 'derived_from_balance'">
        {{ 'BALANCE.AGRONOMY.IDENTITY_DERIVED' | translate }}
      </p>
      <p class="agronomic-status" *ngIf="profile?.identity?.status === 'confirmed'">
        {{ 'BALANCE.AGRONOMY.IDENTITY_CONFIRMED' | translate }}
      </p>
      <p class="agronomic-status" *ngIf="profile?.identity?.status === 'unknown'">
        {{ 'BALANCE.AGRONOMY.IDENTITY_UNKNOWN' | translate }}
      </p>
      <p class="agronomic-status" role="alert" *ngIf="profileSaveBlockReason">
        {{ profileSaveBlockReason | translate }}
      </p>

      <ng-container *ngIf="selectedCropCode">
        <div class="agronomic-fields" *ngIf="hasCatalogVarieties">
          <mat-form-field appearance="outline" *ngIf="varietyOptions.length">
            <mat-label>{{ 'BALANCE.AGRONOMY.KNOWN_VARIETY' | translate }}</mat-label>
            <mat-select
              [value]="profileForm.get('varietyId')?.value"
              [disabled]="optionsLoading"
              (selectionChange)="profileForm.get('varietyId')?.setValue($event.value); onKnownVarietyChange($event.value)"
            >
              <mat-option [value]="null">{{ 'BALANCE.AGRONOMY.VARIETY_NOT_LISTED' | translate }}</mat-option>
              <mat-option *ngFor="let variety of varietyOptions" [value]="variety.id">
                {{ displayName(variety) }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" *ngIf="!isKnownVarietySelected">
            <mat-label>{{ 'BALANCE.AGRONOMY.FREE_VARIETY' | translate }}</mat-label>
            <input matInput maxlength="160" [formControl]="profileForm.get('varietyFreeText')" />
          </mat-form-field>

          <mat-form-field appearance="outline" *ngIf="groupOptions.length && !isKnownVarietySelected">
            <mat-label>{{ 'BALANCE.AGRONOMY.COMPARABLE_GROUP' | translate }}</mat-label>
            <mat-select [formControl]="profileForm.get('varietyGroupId')">
              <mat-option [value]="null">{{ 'BALANCE.AGRONOMY.NO_GROUP' | translate }}</mat-option>
              <mat-option *ngFor="let group of groupOptions" [value]="group.id">
                {{ displayName(group) }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <div class="agronomic-readonly" *ngIf="isKnownVarietySelected">
            <span>{{ 'BALANCE.AGRONOMY.COMPARABLE_GROUP' | translate }}</span>
            <strong>{{ groupName(profileForm.get('varietyGroupId')?.value) }}</strong>
          </div>
        </div>

        <mat-form-field appearance="outline" *ngIf="!hasCatalogVarieties">
          <mat-label>{{ 'BALANCE.AGRONOMY.FREE_VARIETY' | translate }}</mat-label>
          <input matInput maxlength="160" [formControl]="profileForm.get('varietyFreeText')" />
        </mat-form-field>
      </ng-container>

      <div class="agronomic-actions">
        <button mat-flat-button color="primary" type="button" [disabled]="!canSaveProfile" (click)="saveProfile()">
          {{ profileSaving ? ('BALANCE.AGRONOMY.SAVING' | translate) : ('BALANCE.AGRONOMY.SAVE_PROFILE' | translate) }}
        </button>
        <button
          mat-button
          type="button"
          *ngIf="hasExplicitProfile"
          [disabled]="profileClosing || profileSaving"
          (click)="closeProfile()"
        >
          {{ 'BALANCE.AGRONOMY.USE_BALANCE_REFERENCE' | translate }}
        </button>
      </div>
    </div>

    <div class="agronomic-subsection estimated-phenology">
      <h4>{{ 'BALANCE.AGRONOMY.ESTIMATED_TITLE' | translate }}</h4>
      <p class="agronomic-help">{{ 'BALANCE.AGRONOMY.ESTIMATED_HELP' | translate }}</p>
      <div class="agronomic-error" role="alert" *ngIf="estimatedPhenologyError">{{ estimatedPhenologyError }}</div>
      <p class="agronomic-status" *ngIf="estimatedPhenology && estimatedPhenology.region !== 'ES_MEDITERRANEAN_GENERIC'">
        {{ 'BALANCE.AGRONOMY.ESTIMATED_UNSUPPORTED_REGION' | translate }}
      </p>
      <ng-container *ngIf="estimatedPhenology?.region === 'ES_MEDITERRANEAN_GENERIC'">
        <p class="agronomic-status" *ngIf="estimatedPhenology?.estimate?.availability === 'available'; else estimateUnknown">
          <strong>{{ 'BALANCE.AGRONOMY.ESTIMATED_CURRENT' | translate }}:</strong>
          {{ estimatedStageLabel }}
          <ng-container *ngIf="estimatedPhenology?.estimate?.adjacentStage">
            · {{ translatedEstimatedStage(estimatedPhenology?.estimate?.adjacentStage?.stageCode || '') }}
          </ng-container>
          <span> — {{ 'BALANCE.AGRONOMY.ESTIMATED_THEORETICAL' | translate }}</span>
        </p>
        <ng-template #estimateUnknown>
          <p class="agronomic-status">{{ 'BALANCE.AGRONOMY.ESTIMATED_UNKNOWN' | translate }}</p>
        </ng-template>
        <div class="agronomic-fields">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'BALANCE.AGRONOMY.ESTIMATED_SHIFT' | translate }}</mat-label>
            <input matInput type="number" min="-366" max="366" step="1" [formControl]="estimatedPhenologyForm.get('shiftDays')" />
            <mat-hint>{{ 'BALANCE.AGRONOMY.ESTIMATED_SHIFT_HELP' | translate }}</mat-hint>
          </mat-form-field>
          <mat-form-field appearance="outline" *ngIf="hasAnnualCycleStart">
            <mat-label>{{ 'BALANCE.AGRONOMY.CYCLE_START_DATE' | translate }}</mat-label>
            <input matInput readonly [matDatepicker]="cycleStartPicker" [formControl]="estimatedPhenologyForm.get('cycleStartDate')" (click)="cycleStartPicker.open()" />
            <mat-datepicker-toggle matSuffix [for]="cycleStartPicker"></mat-datepicker-toggle>
            <mat-datepicker #cycleStartPicker></mat-datepicker>
          </mat-form-field>
        </div>
        <p class="agronomic-help" *ngIf="hasAnnualCycleStart">{{ 'BALANCE.AGRONOMY.CYCLE_START_HELP' | translate }}</p>
        <div class="agronomic-actions">
          <button mat-flat-button color="primary" type="button" [disabled]="!canSaveEstimatedPhenology" (click)="saveEstimatedPhenology()">
            {{ estimatedPhenologySaving ? ('BALANCE.AGRONOMY.SAVING' | translate) : ('BALANCE.AGRONOMY.SAVE_ESTIMATE' | translate) }}
          </button>
        </div>
      </ng-container>
    </div>

    <mat-expansion-panel class="agronomic-advanced-observations">
      <mat-expansion-panel-header>
        <mat-panel-title>
          {{ 'BALANCE.AGRONOMY.ADVANCED_OBSERVATIONS_TITLE' | translate: { count: observations.length } }}
        </mat-panel-title>
      </mat-expansion-panel-header>

    <div class="agronomic-subsection">
      <h4>{{ 'BALANCE.AGRONOMY.PHENOLOGY_TITLE' | translate }}</h4>
      <p class="agronomic-help">{{ 'BALANCE.AGRONOMY.PHENOLOGY_HELP' | translate }}</p>
      <div class="agronomic-error" role="alert" *ngIf="observationError">{{ observationError }}</div>

      <ng-container *ngIf="selectedCropCode && (selectablePhenologyOptions.length || selectableCampaignOptions.length); else noPhases">
        <div class="agronomic-fields">
          <mat-form-field appearance="outline" class="phase-field">
            <mat-label>{{ 'BALANCE.AGRONOMY.PHASES' | translate }}</mat-label>
            <mat-select [formControl]="observationForm.get('phaseCodes')" multiple>
              <mat-optgroup *ngIf="selectablePhenologyOptions.length" [label]="'BALANCE.AGRONOMY.PHENOLOGY_KIND' | translate">
                <mat-option *ngFor="let phase of selectablePhenologyOptions" [value]="phase.id">
                  {{ translatedPhaseName(phase) }}
                </mat-option>
              </mat-optgroup>
              <mat-optgroup *ngIf="selectableCampaignOptions.length" [label]="'BALANCE.AGRONOMY.CAMPAIGN_KIND' | translate">
                <mat-option *ngFor="let phase of selectableCampaignOptions" [value]="phase.id">
                  {{ translatedPhaseName(phase) }}
                </mat-option>
              </mat-optgroup>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'BALANCE.AGRONOMY.OBSERVED_AT' | translate }}</mat-label>
            <input matInput readonly [matDatepicker]="observedAtPicker" [formControl]="observationForm.get('observedAt')" (click)="observedAtPicker.open()" (dateChange)="onObservedAtChange($event.value)" />
            <mat-datepicker-toggle matSuffix [for]="observedAtPicker"></mat-datepicker-toggle>
            <mat-datepicker #observedAtPicker></mat-datepicker>
          </mat-form-field>
        </div>
        <p class="agronomic-help compact-help">{{ 'BALANCE.AGRONOMY.ONE_DAY_ONLY' | translate }}</p>

        <mat-checkbox [checked]="periodKnown" (change)="setPeriodKnown($event.checked)">
          {{ 'BALANCE.AGRONOMY.KNOWN_PERIOD' | translate }}
        </mat-checkbox>
        <div class="agronomic-fields period-fields" *ngIf="periodKnown">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'BALANCE.AGRONOMY.PERIOD_START' | translate }}</mat-label>
            <input matInput readonly [matDatepicker]="periodStartPicker" [formControl]="observationForm.get('validFrom')" (click)="periodStartPicker.open()" />
            <mat-datepicker-toggle matSuffix [for]="periodStartPicker"></mat-datepicker-toggle>
            <mat-datepicker #periodStartPicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'BALANCE.AGRONOMY.PERIOD_END_INCLUSIVE' | translate }}</mat-label>
            <input matInput readonly [matDatepicker]="periodEndPicker" [formControl]="observationForm.get('validThrough')" (click)="periodEndPicker.open()" />
            <mat-datepicker-toggle matSuffix [for]="periodEndPicker"></mat-datepicker-toggle>
            <mat-datepicker #periodEndPicker></mat-datepicker>
          </mat-form-field>
        </div>
        <div class="agronomic-actions">
          <button mat-flat-button color="primary" type="button" [disabled]="!canSaveObservation" (click)="saveObservation()">
            {{ observationSaving ? ('BALANCE.AGRONOMY.SAVING' | translate) : ('BALANCE.AGRONOMY.SAVE_OBSERVATION' | translate) }}
          </button>
        </div>
      </ng-container>
      <ng-template #noPhases>
        <p class="agronomic-help">{{ selectedCropCode ? ('BALANCE.AGRONOMY.NO_PHASES' | translate) : ('BALANCE.AGRONOMY.SELECT_CROP_FIRST' | translate) }}</p>
      </ng-template>
    </div>

    <div class="agronomic-subsection">
      <h4>{{ 'BALANCE.AGRONOMY.HISTORY_TITLE' | translate }}</h4>
      <p class="agronomic-error" role="alert" *ngIf="historyError">{{ historyError }}</p>
      <p class="agronomic-help" *ngIf="!observations.length && !historyError">
        {{ 'BALANCE.AGRONOMY.NO_OBSERVATIONS' | translate }}
      </p>
      <div class="observation-row" *ngFor="let observation of observations">
        <div>
          <strong>{{ observationPhaseLabels(observation) }}</strong>
          <span>{{ 'BALANCE.AGRONOMY.OBSERVED_ON' | translate }} {{ formatDateOnly(observation.observedAt) }}</span>
          <span>{{ 'BALANCE.AGRONOMY.PERIOD' | translate }} {{ observationPeriod(observation) }}</span>
          <span>{{ observationStatus(observation) | translate }}</span>
        </div>
        <button
          mat-button
          type="button"
          *ngIf="canFinishObservation(observation)"
          [disabled]="closingObservationId !== null || deletingObservationId !== null"
          [matTooltip]="'BALANCE.AGRONOMY.FINISH_OBSERVATION_HELP' | translate"
          (click)="finishObservation(observation)"
        >
          {{ closingObservationId === observation.idModulePhenologyObservation ? ('BALANCE.AGRONOMY.SAVING' | translate) : ('BALANCE.AGRONOMY.FINISH_OBSERVATION' | translate) }}
        </button>
        <button
          mat-button
          color="warn"
          type="button"
          [disabled]="closingObservationId !== null || deletingObservationId !== null"
          [attr.aria-label]="'BALANCE.AGRONOMY.DELETE_OBSERVATION' | translate"
          [matTooltip]="'BALANCE.AGRONOMY.DELETE_OBSERVATION_HELP' | translate"
          (click)="requestDeleteObservation(observation)"
        >
          <mat-icon>delete</mat-icon>
          {{ deletingObservationId === observation.idModulePhenologyObservation ? ('BALANCE.AGRONOMY.SAVING' | translate) : ('BALANCE.AGRONOMY.DELETE_OBSERVATION' | translate) }}
        </button>
      </div>
    </div>
    </mat-expansion-panel>
  </ng-container>

  <ng-template #deleteObservationDialog>
    <h2 mat-dialog-title>{{ 'BALANCE.AGRONOMY.DELETE_OBSERVATION_TITLE' | translate }}</h2>
    <mat-dialog-content>
      <p>{{ 'BALANCE.AGRONOMY.DELETE_OBSERVATION_CONFIRM' | translate }}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="cancelDeleteObservation()">
        {{ 'COMMON.CANCEL' | translate }}
      </button>
      <button mat-flat-button color="warn" type="button" (click)="confirmDeleteObservation()">
        {{ 'BALANCE.AGRONOMY.DELETE_OBSERVATION' | translate }}
      </button>
    </mat-dialog-actions>
  </ng-template>
</section>
